import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ProtectedRoute = ({ children, requiredRoles = [] }) => {
  const { isAuthenticated, loading, profile, requiresPasswordChange, connectionError } = useAuth();
  const location = useLocation();

  // CRITICAL: Reduce timeout from 10 to 8 seconds for faster feedback
  const [loadTimeout, setLoadTimeout] = React.useState(false);

  React.useEffect(() => {
    if (loading) {
      console.log('🔐 ProtectedRoute: Authentication loading...');
      const timer = setTimeout(() => {
        console.error('⏰ ProtectedRoute: Loading timeout after 8 seconds');
        setLoadTimeout(true);
      }, 8000); // REDUCED from 10000 to 8000

      return () => clearTimeout(timer);
    } else {
      console.log('🔐 ProtectedRoute: Authentication complete', {
        isAuthenticated,
        requiresPasswordChange,
        connectionError
      });
      setLoadTimeout(false);
    }
  }, [loading, isAuthenticated, requiresPasswordChange, connectionError]);

  // Show loading spinner with timeout protection
  if (loading && !loadTimeout) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // CRITICAL: Handle timeout case - redirect to login
  if (loadTimeout) {
    console.error('❌ ProtectedRoute: Authentication verification timeout, redirecting to login');
    return <Navigate to="/login" state={{ from: location, error: 'Authentication timeout. Please try logging in again.' }} replace />;
  }

  // Show error if connection failed
  if (connectionError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg max-w-md">
            <h3 className="font-bold text-lg mb-2">Connection Error</h3>
            <p className="mb-4">{connectionError}</p>
            <button 
              onClick={() => window.location?.reload()}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Redirect to change password if required
  if (requiresPasswordChange && location?.pathname !== '/auth/change-password') {
    console.log('🔐 ProtectedRoute: Password change required, redirecting...');
    return <Navigate to="/auth/change-password" replace />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    console.log('🔐 ProtectedRoute: Not authenticated, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (profile?.status && profile.status !== 'active') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-amber-50 border border-amber-300 text-amber-900 px-6 py-4 rounded-lg max-w-md">
          <h3 className="font-bold text-lg mb-2">Account unavailable</h3>
          <p>Your account is {profile.status}. Contact the system administrator for assistance.</p>
        </div>
      </div>
    );
  }

  const userRoles = [profile?.primary_role, ...(profile?.secondary_roles || [])].filter(Boolean);
  const hasRequiredRole = requiredRoles.length === 0 || requiredRoles.some((role) => userRoles.includes(role));

  if (!hasRequiredRole) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-red-50 border border-red-300 text-red-800 px-6 py-4 rounded-lg max-w-md">
          <h3 className="font-bold text-lg mb-2">Access denied</h3>
          <p>Your account does not have permission to access this module.</p>
        </div>
      </div>
    );
  }

  // All checks passed - render protected content
  console.log('✅ ProtectedRoute: Access granted');
  return children;
};

export default ProtectedRoute;
